import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import i18n from "@/i18n";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users,
  CreditCard, AlertTriangle, Server, Activity, LifeBuoy, ArrowUpRight, Wallet,
  Receipt, PiggyBank, Percent, ShieldAlert, Bell, Info, XCircle, CheckCircle2,
  Clock, ScrollText, Cpu,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const loc = () => ((i18n.language || "fr").startsWith("fr") ? "fr-FR" : "en-US");
const fmtNum = (n) => Number(n || 0).toLocaleString(loc());
const fmtMoney = (v) => (v === null || v === undefined ? "Data unavailable" : money(v));
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const fmtDate = (d, withTime = true) =>
  d ? new Date(d).toLocaleString(loc(), withTime
    ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short" }) : "—";

const PERIODS = [
  { k: "today", l: "Aujourd'hui" }, { k: "yesterday", l: "Hier" },
  { k: "7d", l: "7 jours" }, { k: "30d", l: "30 jours" }, { k: "90d", l: "90 jours" },
];

const STATUS_TINT = {
  HEALTHY: "text-emerald-600 dark:text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
  WARNING: "text-amber-600 dark:text-amber-400 bg-amber-500/15 border-amber-500/30",
  DEGRADED: "text-orange-600 dark:text-orange-400 bg-orange-500/15 border-orange-500/30",
  OFFLINE: "text-rose-600 dark:text-rose-400 bg-rose-500/15 border-rose-500/30",
  ERROR: "text-rose-600 dark:text-rose-400 bg-rose-500/15 border-rose-500/30",
  DISABLED: "text-slate-500 dark:text-slate-400 bg-slate-500/15 border-slate-500/30",
  UNKNOWN: "text-slate-500 dark:text-slate-400 bg-slate-500/15 border-slate-500/30",
};
const ORDER_TINT = {
  completed: "text-emerald-600 dark:text-emerald-400",
  partial: "text-indigo-600 dark:text-indigo-400",
  failed: "text-rose-600 dark:text-rose-400",
  provider_unavailable: "text-rose-600 dark:text-rose-400",
  pending: "text-amber-600 dark:text-amber-400",
  processing: "text-blue-600 dark:text-blue-400",
  in_progress: "text-blue-600 dark:text-blue-400",
  cancelled: "text-slate-500 dark:text-slate-400",
  refunded: "text-fuchsia-600 dark:text-fuchsia-400",
};

// ---------- small UI atoms ------------------------------------------------
function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-400">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {up ? "+" : ""}{pct}%
    </span>
  );
}

function Kpi({ icon: Icon, label, value, delta, tint, onClick, testid, unavailable }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-blue-500/50 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-blue-500/50">
      <div className="flex items-center justify-between">
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${tint}`}><Icon className="h-4 w-4" /></span>
        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-blue-500 dark:text-slate-600" />
      </div>
      <div className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 font-display font-extrabold tracking-tight ${unavailable ? "text-sm text-slate-400 dark:text-slate-500" : "text-2xl text-slate-900 lg:text-[1.7rem] dark:text-white"}`}>{value}</div>
      <div className="mt-1.5">{delta !== false && <Delta pct={delta} />}</div>
    </button>
  );
}

function Section({ title, icon: Icon, action, children, testid, className = "" }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-3.5 dark:border-slate-800">
        <h3 className="flex items-center gap-2 font-display text-sm font-semibold text-slate-900 dark:text-white">
          {Icon && <Icon className="h-4 w-4 text-blue-500" />}{title}
        </h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const NoData = ({ label }) => {
  const { t } = useContent();
  return <div className="flex h-full min-h-[120px] items-center justify-center py-8 text-center text-sm text-slate-400">{label || t("admin.cc.no_data", "Pas encore assez de données")}</div>;
};

const CHART_TT = { background: "rgba(15,23,42,0.95)", border: "1px solid #334155", borderRadius: 10, color: "#fff", fontSize: 12 };

// ---------- page ----------------------------------------------------------
export default function CommandCenter() {
  const nav = useNavigate();
  const { t: ct } = useContent();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [svcMetric, setSvcMetric] = useState("revenue");
  const [chartSeries, setChartSeries] = useState("revenue"); // revenue|profit

  const loadPeriodic = useCallback(async (p) => {
    const g = (u) => api.get(u).then((r) => r.data).catch(() => null);
    const [overview, revenue, orders, payments, customers, services] = await Promise.all([
      g(`/admin/dashboard/overview?period=${p}`),
      g(`/admin/dashboard/revenue-series?period=${p}`),
      g(`/admin/dashboard/orders?period=${p}`),
      g(`/admin/dashboard/payments?period=${p}`),
      g(`/admin/dashboard/customers?period=${p}`),
      g(`/admin/dashboard/services?metric=${svcMetric}&period=${p}`),
    ]);
    setData((d) => ({ ...d, overview, revenue, orders, payments, customers, services }));
  }, [svcMetric]);

  const loadStatic = useCallback(async () => {
    const g = (u) => api.get(u).then((r) => r.data).catch(() => null);
    const [providers, alerts, support, system] = await Promise.all([
      g(`/admin/dashboard/providers`), g(`/admin/dashboard/alerts`),
      g(`/admin/dashboard/support`), g(`/admin/dashboard/system`),
    ]);
    setData((d) => ({ ...d, providers, alerts, support, system }));
  }, []);

  const loadActivity = useCallback(async () => {
    const r = await api.get(`/admin/dashboard/activity`).then((r) => r.data).catch(() => null);
    setData((d) => ({ ...d, activity: r }));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadPeriodic(period), loadStatic(), loadActivity()]);
    setRefreshedAt(new Date());
    setLoading(false);
  }, [period, loadPeriodic, loadStatic, loadActivity]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [period]);
  // re-fetch services list when metric changes
  useEffect(() => {
    api.get(`/admin/dashboard/services?metric=${svcMetric}&period=${period}`)
      .then((r) => setData((d) => ({ ...d, services: r.data }))).catch(() => {});
    /* eslint-disable-next-line */
  }, [svcMetric]);
  // live activity auto-refresh every 10s
  useEffect(() => {
    const id = setInterval(loadActivity, 10000);
    return () => clearInterval(id);
  }, [loadActivity]);

  const ov = data.overview || {};
  const fin = ov.finance_visible;

  if (loading && !data.overview) {
    return <div className="py-24 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="command-center">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl dark:text-white">TWP COMMAND CENTER</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Vue globale de The World Promotions</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400">Actualisé : {refreshedAt ? fmtDate(refreshedAt) : "—"}</span>
          <button data-testid="cc-refresh" onClick={refresh}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
      </div>

      {/* Period selector */}
      <RiskBanner />
      <div className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800" data-testid="cc-period">
        {PERIODS.map((p) => (
          <button key={p.k} data-testid={`cc-period-${p.k}`} onClick={() => setPeriod(p.k)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${period === p.k ? "bg-white text-blue-600 shadow-sm dark:bg-slate-700 dark:text-blue-400" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"}`}>
            {ct(`admin.cc.period.${p.k}`, p.l)}
          </button>
        ))}
      </div>

      {/* Alert center (top priority) */}
      <AlertCenter alerts={data.alerts} nav={nav} />
      <ReceiptEmailFailedIndicator nav={nav} />

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {fin && <Kpi testid="kpi-revenue" icon={DollarSign} label="Revenue" value={fmtMoney(ov.revenue?.value)} delta={ov.revenue?.delta_pct} tint="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" onClick={() => nav("/admin/transactions")} />}
        {fin && <Kpi testid="kpi-cost" icon={Wallet} label="Provider Cost (brut)" value={fmtMoney(ov.provider_cost?.value)} delta={ov.provider_cost?.delta_pct} tint="bg-blue-500/15 text-blue-600 dark:text-blue-400" onClick={() => nav("/admin/providers")} />}
        {fin && <Kpi testid="kpi-funding" icon={Percent} label="Funding Fee" value={fmtMoney(ov.provider_funding_cost?.value)} delta={ov.provider_funding_cost?.delta_pct} tint="bg-amber-500/15 text-amber-600 dark:text-amber-400" onClick={() => nav("/admin/providers")} />}
        {fin && <Kpi testid="kpi-eff-cost" icon={Wallet} label="Coût fournisseur effectif" value={fmtMoney(ov.effective_provider_cost?.value)} delta={ov.effective_provider_cost?.delta_pct} tint="bg-blue-600/15 text-blue-700 dark:text-blue-300" onClick={() => nav("/admin/providers")} />}
        {fin && <Kpi testid="kpi-fees" icon={Receipt} label={ov.payment_fees?.estimated || ov.payment_fees?.note === "estimated_from_config" ? "Payment Fees (est. 5%)" : "Payment Fees"} value={ov.payment_fees?.unavailable ? "Data unavailable" : fmtMoney(ov.payment_fees?.value)} unavailable={ov.payment_fees?.unavailable} delta={false} tint="bg-slate-500/15 text-slate-500 dark:text-slate-400" onClick={() => nav("/admin/payments")} />}
        {fin && <Kpi testid="kpi-refunds" icon={CreditCard} label="Refunds" value={fmtMoney(ov.refunds?.value)} delta={ov.refunds?.delta_pct} tint="bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400" onClick={() => nav("/admin/refunds")} />}
        {fin && <Kpi testid="kpi-profit" icon={PiggyBank} label="Contribution Profit" value={fmtMoney(ov.contribution_profit?.value)} delta={ov.contribution_profit?.delta_pct} tint="bg-teal-500/15 text-teal-600 dark:text-teal-400" onClick={() => nav("/admin/transactions")} />}
        {fin && <Kpi testid="kpi-margin" icon={Percent} label="Contribution Margin %" value={fmtPct(ov.contribution_margin?.value)} delta={false} tint="bg-violet-500/15 text-violet-600 dark:text-violet-400" onClick={() => nav("/admin/transactions")} />}
        <Kpi testid="kpi-orders" icon={ShoppingCart} label="Orders" value={fmtNum(ov.orders?.value)} delta={ov.orders?.delta_pct} tint="bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" onClick={() => nav("/admin/orders")} />
        <Kpi testid="kpi-customers" icon={Users} label="Active Customers" value={fmtNum(ov.active_customers?.value)} delta={ov.active_customers?.delta_pct} tint="bg-cyan-500/15 text-cyan-600 dark:text-cyan-400" onClick={() => nav("/admin/users")} />
        <Kpi testid="kpi-pending-pay" icon={Clock} label="Pending Payments" value={fmtNum(ov.pending_payments?.value)} delta={false} tint="bg-amber-500/15 text-amber-600 dark:text-amber-400" onClick={() => nav("/admin/payment-alerts")} />
        <Kpi testid="kpi-attention" icon={ShieldAlert} label="Orders Requiring Attention" value={fmtNum(ov.orders_attention?.value)} delta={false} tint="bg-rose-500/15 text-rose-600 dark:text-rose-400" onClick={() => nav("/admin/orders")} />
      </div>
      {fin && (
        <div data-testid="cc-profit-formula" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
          {ct("admin.cc.formula", "Revenue − Refunds − Payment Fees (est. 5%) − Coût fournisseur effectif = Contribution Profit")}
          <span className="mx-1 opacity-50">·</span>
          {ct("admin.cc.formula_eff", "Coût fournisseur effectif = coût brut + frais de financement")}
        </div>
      )}

      {/* Revenue & profit analytics */}
      {fin && <RevenueAnalytics revenue={data.revenue} series={chartSeries} setSeries={setChartSeries} />}

      {/* Orders control */}
      <OrdersControl orders={data.orders} nav={nav} />

      {/* Provider control */}
      <ProviderControl providers={data.providers} nav={nav} />

      {/* Payment control */}
      <PaymentControl payments={data.payments} nav={nav} />

      {/* Service intelligence */}
      <ServiceIntelligence services={data.services} metric={svcMetric} setMetric={setSvcMetric} fin={fin} nav={nav} />

      <div className="grid gap-6 lg:grid-cols-2">
        <CustomerOverview customers={data.customers} fin={fin} nav={nav} />
        <SupportSection support={data.support} nav={nav} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <LiveActivity activity={data.activity} />
        <SystemHealth system={data.system} />
      </div>
    </div>
  );
}

// ---------- Risk alert banner (P1 center synthetic indicator) -------------
function RiskBanner() {
  const nav = useNavigate();
  const { t } = useContent();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/risk/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s || (!s.high && !s.critical)) return null;
  return (
    <button onClick={() => nav("/admin/risk")} data-testid="cc-risk-banner"
      className="flex w-full items-center justify-between gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left transition-colors hover:bg-amber-500/15">
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-500" />
        <div>
          <div className="text-sm font-semibold text-amber-700 dark:text-amber-400">{t("admin.cc.risk.title", "Alertes risque à examiner")}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            <span className="font-bold text-rose-500">{s.critical}</span> CRITICAL · <span className="font-bold text-amber-500">{s.high}</span> HIGH · {s.open_high_critical} {t("admin.cc.risk.unresolved", "non résolue(s)")}
          </div>
        </div>
      </div>
      <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400">{t("admin.cc.risk.open_center", "Ouvrir le centre")} <ArrowUpRight className="h-4 w-4" /></span>
    </button>
  );
}

// ---------- Receipt email failed indicator (P1.1 operational) -------------
function ReceiptEmailFailedIndicator({ nav }) {
  const { t } = useTranslation();
  const [n, setN] = useState(null);
  useEffect(() => {
    api.get("/admin/partner/withdrawals/receipt-failed-count")
      .then((r) => setN(r.data?.receipt_email_failed_count ?? 0))
      .catch(() => setN(null));
  }, []);
  if (n === null) return null;
  const failed = n > 0;
  return (
    <button
      onClick={() => nav("/admin/partner-withdrawals?receipt_email=failed")}
      data-testid="cc-receipt-failed-indicator"
      className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
        failed
          ? "border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/60"
      }`}
    >
      <div className="flex items-center gap-3">
        <Receipt className={`h-5 w-5 ${failed ? "text-rose-500" : "text-slate-400"}`} />
        <div>
          <div className={`text-sm font-semibold ${failed ? "text-rose-700 dark:text-rose-400" : "text-slate-700 dark:text-slate-200"}`}>
            {t("admin.cc.receipt_failed.title")} : <span data-testid="cc-receipt-failed-count">{n}</span>
          </div>
          {failed && (
            <div className="text-xs text-slate-500 dark:text-slate-400">{t("admin.cc.receipt_failed.sub")}</div>
          )}
        </div>
      </div>
      {failed && (
        <span className="flex items-center gap-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
          Retraits <ArrowUpRight className="h-4 w-4" />
        </span>
      )}
    </button>
  );
}

// ---------- Alert Center --------------------------------------------------
function AlertCenter({ alerts, nav }) {
  const { t } = useContent();
  if (!alerts) return null;
  const s = alerts.summary || {};
  const sevMeta = {
    CRITICAL: { icon: XCircle, tint: "text-rose-500", badge: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30" },
    WARNING: { icon: AlertTriangle, tint: "text-amber-500", badge: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" },
    INFO: { icon: Info, tint: "text-blue-500", badge: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  };
  return (
    <Section testid="cc-alert-center" title="ALERT CENTER" icon={Bell}
      action={
        <div className="flex gap-2 text-[11px] font-bold">
          <span className="rounded-full border border-rose-500/30 bg-rose-500/15 px-2 py-0.5 text-rose-600 dark:text-rose-400">{s.critical || 0} {t("admin.cc.alert.critical", "critique")}</span>
          <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-amber-600 dark:text-amber-400">{s.warning || 0} {t("admin.cc.alert.warning", "alerte")}</span>
          <span className="rounded-full border border-blue-500/30 bg-blue-500/15 px-2 py-0.5 text-blue-600 dark:text-blue-400">{s.info || 0} {t("admin.cc.alert.info", "info")}</span>
        </div>
      }>
      {(!alerts.alerts || alerts.alerts.length === 0)
        ? <NoData label={t("admin.cc.alert.none", "Aucune alerte active 🎉")} />
        : (
          <div className="grid gap-2 md:grid-cols-2">
            {alerts.alerts.slice(0, 12).map((a, i) => {
              const m = sevMeta[a.severity] || sevMeta.INFO;
              return (
                <button key={i} data-testid={`cc-alert-${i}`} onClick={() => nav(a.source === "provider" ? "/admin/providers" : a.source === "payment" ? "/admin/payment-alerts" : a.source === "order" ? "/admin/orders" : "/admin/alerts")}
                  className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-left transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50">
                  <m.icon className={`mt-0.5 h-4 w-4 shrink-0 ${m.tint}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded border px-1.5 py-0.5 text-[10px] font-bold ${m.badge}`}>{a.severity}</span>
                      <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{a.title}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{a.description} {a.timestamp && `· ${fmtDate(a.timestamp)}`}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
    </Section>
  );
}

// ---------- Revenue Analytics ---------------------------------------------
function RevenueAnalytics({ revenue, series, setSeries }) {
  const enough = revenue?.enough_data && (revenue?.series || []).length > 0;
  const t = revenue?.totals;
  return (
    <Section testid="cc-revenue" title="Revenue & Profit Analytics" icon={TrendingUp}
      action={
        <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
          {[{ k: "revenue", l: "Revenue/Cost/Profit" }, { k: "margin", l: "Marge %" }].map((o) => (
            <button key={o.k} onClick={() => setSeries(o.k)} data-testid={`cc-rev-mode-${o.k}`}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${series === o.k ? "bg-white text-blue-600 shadow-sm dark:bg-slate-700 dark:text-blue-400" : "text-slate-500 dark:text-slate-400"}`}>{o.l}</button>
          ))}
        </div>
      }>
      {!enough ? <NoData /> : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Revenue", t.revenue], ["Coût brut", t.provider_cost], ["Funding", t.funding_cost], ["Coût effectif", (t.provider_cost || 0) + (t.funding_cost || 0)], ["Remboursements", t.refunds], ["Profit", t.profit]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                <div className="text-[11px] uppercase text-slate-400">{l}</div>
                <div className="mt-0.5 font-display text-base font-bold text-slate-900 dark:text-white">{fmtMoney(v)}</div>
              </div>
            ))}
            <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
              <div className="text-[11px] uppercase text-slate-400">Marge %</div>
              <div className="mt-0.5 font-display text-base font-bold text-slate-900 dark:text-white">{fmtPct(t.margin)}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            {series === "revenue" ? (
              <AreaChart data={revenue.series} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.3} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gProf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#94A3B8" strokeOpacity={0.15} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} tickFormatter={(d) => fmtDate(d, false)} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                <Tooltip contentStyle={CHART_TT} formatter={(v, n) => [money(v), n]} labelFormatter={(d) => fmtDate(d, false)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area name="Revenue" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#gRev)" />
                <Area name="Coût" type="monotone" dataKey="provider_cost" stroke="#F59E0B" strokeWidth={2} fillOpacity={0} />
                <Area name="Profit" type="monotone" dataKey="profit" stroke="#3B82F6" strokeWidth={2} fill="url(#gProf)" />
              </AreaChart>
            ) : (
              <AreaChart data={revenue.series} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#94A3B8" strokeOpacity={0.15} vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} tickFormatter={(d) => fmtDate(d, false)} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={CHART_TT} formatter={(v) => [`${v}%`, "Marge"]} labelFormatter={(d) => fmtDate(d, false)} />
                <Area name="Marge %" type="monotone" dataKey="margin" stroke="#8B5CF6" strokeWidth={2} fill="none" />
              </AreaChart>
            )}
          </ResponsiveContainer>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
                {["Date", "Revenue", "Coût brut", "Funding", "Coût eff.", "Frais (est.)", "Remb.", "Profit", "Marge %"].map((h) => <th key={h} className="px-2 pb-2 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {revenue.series.filter((r) => r.revenue > 0 || r.profit !== 0).slice(-14).map((r) => (
                  <tr key={r.date} className="border-b border-slate-100 last:border-0 dark:border-slate-800/60">
                    <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{fmtDate(r.date, false)}</td>
                    <td className="px-2 py-2">{money(r.revenue)}</td>
                    <td className="px-2 py-2">{money(r.provider_cost)}</td>
                    <td className="px-2 py-2 text-amber-600 dark:text-amber-400">{money(r.funding_cost)}</td>
                    <td className="px-2 py-2">{money((r.provider_cost || 0) + (r.funding_cost || 0))}</td>
                    <td className="px-2 py-2">{r.fees ? money(r.fees) : <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-2">{money(r.refunds)}</td>
                    <td className="px-2 py-2 font-medium">{money(r.profit)}</td>
                    <td className="px-2 py-2">{fmtPct(r.margin)}</td>
                  </tr>
                ))}
                {revenue.series.filter((r) => r.revenue > 0).length === 0 && (
                  <tr><td colSpan={9} className="py-6 text-center text-slate-400">Pas encore assez de données</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

// ---------- Orders Control ------------------------------------------------
function OrdersControl({ orders, nav }) {
  const { t: ordT } = useContent();
  if (!orders) return null;
  const counts = orders.counts || {};
  return (
    <Section testid="cc-orders" title="Orders Control Center" icon={ShoppingCart}
      action={<button onClick={() => nav("/admin/orders")} className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">Tout voir →</button>}>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
        {[["Total", orders.total, "slate"], ...Object.entries(counts).map(([k, v]) => [k, v, null])].map(([label, val], i) => (
          <button key={i} onClick={() => nav("/admin/orders")}
            className="rounded-lg border border-slate-200 p-2.5 text-center transition-colors hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50">
            <div className={`font-display text-lg font-bold ${ORDER_TINT[label] || "text-slate-900 dark:text-white"}`}>{fmtNum(val)}</div>
            <div className="mt-0.5 text-[10px] capitalize text-slate-500 dark:text-slate-400">{String(label).replace(/_/g, " ")}</div>
          </button>
        ))}
      </div>
      <div className="mt-5">
        <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase text-rose-600 dark:text-rose-400"><ShieldAlert className="h-4 w-4" /> Orders Requiring Attention ({orders.attention_count})</h4>
        {(!orders.attention || orders.attention.length === 0)
          ? <div className="rounded-lg border border-dashed border-slate-200 py-4 text-center text-sm text-slate-400 dark:border-slate-800">{ordT("admin.cc.no_orders", "Aucune commande à traiter")}</div>
          : (
            <div className="space-y-1.5">
              {orders.attention.map((o) => (
                <button key={o.id} data-testid={`cc-attention-${o.order_number}`} onClick={() => nav("/admin/orders")}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50/50 p-2.5 text-left transition-colors hover:bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/20 dark:hover:bg-rose-950/40">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">#{o.order_number} · {o.service_name}</div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {o.reasons.map((r) => <span key={r} className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-400">{r.replace(/_/g, " ")}</span>)}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-slate-700 dark:text-slate-200">{money(o.price)}</span>
                </button>
              ))}
            </div>
          )}
      </div>
    </Section>
  );
}

// ---------- Provider Control ----------------------------------------------
function ProviderControl({ providers, nav }) {
  const { t: pt } = useContent();
  if (!providers) return null;
  const list = providers.providers || [];
  const chart = list.filter((p) => p.orders > 0).map((p) => ({ name: p.name, Complétées: p.completed_orders, Partielles: p.partial_orders, Échecs: p.failed_orders }));
  return (
    <Section testid="cc-providers" title="Provider Control Center" icon={Server}
      action={<button onClick={() => nav("/admin/providers")} className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">{pt("admin.cc.manage", "Gérer")} →</button>}>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => (
          <button key={p.id} data-testid={`cc-provider-${p.name}`} onClick={() => nav("/admin/providers")}
            className="rounded-xl border border-slate-200 p-4 text-left transition-all hover:border-blue-500/40 hover:shadow-md dark:border-slate-800 dark:hover:border-blue-500/40">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900 dark:text-white">{p.name}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${STATUS_TINT[p.status]}`}>{p.status}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs">
              {p.balance !== undefined && <><span className="text-slate-400">Solde</span><span className={`text-right font-medium ${p.balance < 10 ? "text-rose-500" : p.balance < 50 ? "text-amber-500" : "text-slate-700 dark:text-slate-200"}`}>{money(p.balance)}</span></>}
              <span className="text-slate-400">{pt("admin.cc.error_rate", "Taux d'erreur")}</span><span className="text-right font-medium text-slate-700 dark:text-slate-200">{p.api_health.error_rate}%</span>
              <span className="text-slate-400">{pt("admin.cc.published_services", "Services publiés")}</span><span className="text-right text-slate-700 dark:text-slate-200">{fmtNum(p.published_services)} / {fmtNum(p.imported_services)}</span>
              <span className="text-slate-400">{pt("admin.cc.orders", "Commandes")}</span><span className="text-right text-slate-700 dark:text-slate-200">{fmtNum(p.orders)}</span>
              {p.provider_cost !== undefined && <><span className="text-slate-400">{pt("admin.cc.total_cost", "Coût total")}</span><span className="text-right text-slate-700 dark:text-slate-200">{money(p.provider_cost)}</span></>}
              <span className="text-slate-400">{pt("admin.cc.funding_fee", "Frais de financement")}</span><span className="text-right text-slate-700 dark:text-slate-200">{p.funding_pct != null ? p.funding_pct + "%" : "—"}</span>
            </div>
            <div className="mt-2 text-[10px] text-slate-400">{pt("admin.cc.last_check", "Dernier check :")} {fmtDate(p.last_health_check)}</div>
            {p.reason_message && p.status !== "HEALTHY" && (
              <div className="mt-1 rounded bg-rose-500/10 px-2 py-1 text-[10px] text-rose-600 dark:text-rose-400" data-testid={`cc-provider-why-${p.name}`}>
                <span className="font-semibold">{pt("admin.cc.why_unavailable", "Pourquoi indisponible ?")}</span> {p.reason_message}{p.reason_code ? ` (${p.reason_code})` : ""}
              </div>
            )}
          </button>
        ))}
      </div>
      <div className="mt-5">
        <h4 className="mb-2 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">{pt("admin.cc.provider_perf", "Performance des fournisseurs")}</h4>
        {chart.length === 0 ? <NoData /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chart} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#94A3B8" strokeOpacity={0.15} vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} tickLine={false} axisLine={false} width={36} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TT} cursor={{ fill: "rgba(148,163,184,0.1)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Complétées" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Partielles" fill="#6366F1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Échecs" fill="#F43F5E" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Section>
  );
}

// ---------- Payment Control -----------------------------------------------
function PaymentControl({ payments, nav }) {
  const { t: payT } = useContent();
  if (!payments) return null;
  const rows = payments.methods || [];
  const t = payments.totals || {};
  const att = payments.attention || {};
  const drill = (m) => nav(m.includes("PawaPay") ? "/admin/pawapay" : m.includes("AfriPay") ? "/admin/afripay" : m.includes("USDT") ? "/admin/usdt" : "/admin/payments");
  return (
    <Section testid="cc-payments" title="Payment Control Center" icon={CreditCard}
      action={<button onClick={() => nav("/admin/payments")} className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">{payT("admin.cc.detail", "Détail")} →</button>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            {["Méthode", "Transactions", "Brut", "Frais", "Remb.", "Net"].map((h) => <th key={h} className="px-2 pb-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Pas encore assez de données</td></tr>}
            {rows.map((r) => (
              <tr key={r.method} data-testid={`cc-pm-${r.method}`} onClick={() => drill(r.method)}
                className="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/50">
                <td className="px-2 py-2.5 font-medium text-slate-800 dark:text-slate-100">{r.method}</td>
                <td className="px-2 py-2.5">{fmtNum(r.transactions)}</td>
                <td className="px-2 py-2.5">{money(r.gross)}</td>
                <td className="px-2 py-2.5">{r.fees === null || r.fees === undefined ? <span className="text-slate-400">N/A</span> : money(r.fees)}</td>
                <td className="px-2 py-2.5">{fmtNum(r.refunded)}</td>
                <td className="px-2 py-2.5 font-medium">{money(r.net)}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="font-semibold text-slate-900 dark:text-white">
                <td className="px-2 py-2.5">Total</td><td className="px-2 py-2.5">{fmtNum(t.transactions)}</td>
                <td className="px-2 py-2.5">{money(t.gross)}</td><td className="px-2 py-2.5">{t.fees === null || t.fees === undefined ? <span className="text-slate-400">—</span> : money(t.fees)}</td>
                <td className="px-2 py-2.5">{fmtNum(t.refunded)}</td><td className="px-2 py-2.5">{money(t.net)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
        <button onClick={() => nav("/admin/payment-alerts")} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 font-medium text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" /> {att.unhandled_alerts || 0} alerte(s) à traiter
        </button>
        <span className="text-slate-500 dark:text-slate-400">{(att.long_pending || []).length} paiement(s) en attente {'>'} 2h</span>
        <span className="text-slate-400" data-testid="cc-pm-fee-note">{payT("admin.cc.fee_na_note", "N/A — frais réels non communiqués par la passerelle")}</span>
      </div>
    </Section>
  );
}

// ---------- Service Intelligence ------------------------------------------
function WhyServiceDialog({ sid, onClose }) {
  const { t: wt } = useContent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!sid) { setData(null); return; }
    setLoading(true);
    api.get(`/admin/services/${sid}/candidates`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [sid]);
  return (
    <Dialog open={!!sid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cc-why-dialog">
        <DialogHeader><DialogTitle>{wt("admin.cc.why_title", "Diagnostic de disponibilité")}</DialogTitle></DialogHeader>
        {loading ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : data ? (
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{data.service?.name}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${data.availability?.available ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`}>
                {data.availability?.available ? wt("admin.cc.available", "Disponible") : wt("admin.cc.unavailable", "Indisponible")}
              </span>
            </div>
            {!data.availability?.available && data.availability?.reason_message && (
              <div className="rounded bg-rose-500/10 px-3 py-2 text-rose-600 dark:text-rose-400" data-testid="cc-why-reason">
                {data.availability.reason_message} <span className="text-[10px] opacity-70">({data.availability.reason_code})</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-500">
              <span>{wt("admin.cc.primary", "Principal")}</span><span className="text-right text-slate-700 dark:text-slate-200">{data.primary_provider || "—"}</span>
              <span>{wt("admin.cc.backup", "Secours")}</span><span className="text-right text-slate-700 dark:text-slate-200">{data.backup_provider || "—"}</span>
              <span>{wt("admin.cc.current", "Actuel")}</span><span className="text-right text-slate-700 dark:text-slate-200">{data.current_provider || "—"}</span>
              <span>{wt("admin.cc.max_fundable", "Qté max finançable")}</span><span className="text-right text-slate-700 dark:text-slate-200">{fmtNum(data.availability?.max_fundable_quantity)}</span>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase text-slate-500">{wt("admin.cc.providers", "Fournisseurs")}</div>
              <div className="space-y-1">
                {(data.candidates || []).map((c) => (
                  <div key={c.provider_id} className="flex items-center justify-between rounded border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">
                    <span className="text-slate-700 dark:text-slate-200">{c.provider_name}</span>
                    <span className={c.compatible ? "text-emerald-500" : "text-rose-500"}>{c.compatible ? wt("admin.cc.ok", "OK") : (c.reason_code || "—")}</span>
                  </div>
                ))}
                {(data.candidates || []).length === 0 && <div className="text-xs text-slate-400">{wt("admin.cc.no_provider", "Aucun fournisseur mappé")}</div>}
              </div>
            </div>
          </div>
        ) : <div className="text-sm text-slate-400">{wt("admin.cc.no_data", "Aucune donnée")}</div>}
      </DialogContent>
    </Dialog>
  );
}

function ServiceIntelligence({ services, metric, setMetric, fin, nav }) {
  const { t: svcT } = useContent();
  const [whyId, setWhyId] = useState(null);
  if (!services) return null;
  const list = services.services || [];
  const metrics = [{ k: "revenue", l: "Revenue", fin: true }, { k: "profit", l: "Profit", fin: true }, { k: "orders", l: "Commandes", fin: false }, { k: "margin", l: "Marge", fin: true }, { k: "failures", l: "Échecs", fin: false }, { k: "partial", l: "Partielles", fin: false }, { k: "refunds", l: "Remb.", fin: true }].filter((m) => fin || !m.fin);
  return (
    <>
    <Section testid="cc-services" title="Service Intelligence" icon={Activity}
      action={
        <select data-testid="cc-svc-metric" value={metric} onChange={(e) => setMetric(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {metrics.map((m) => <option key={m.k} value={m.k}>Top par {m.l}</option>)}
        </select>
      }>
      {list.length === 0 ? <NoData /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.service", "Service")}</th>
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.revenue", "Revenue")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.cost_gross", "Coût brut")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.funding", "Funding")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.cost_eff", "Coût eff.")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.fees_est", "Frais (est.)")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.refunds", "Remb.")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.profit", "Contrib.")}</th>}
              {fin && <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.margin", "Marge")}</th>}
              <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.cmd", "Cmd")}</th>
              <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.compl", "Compl.")}</th>
              <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.part", "Part.")}</th>
              <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.fails", "Échecs")}</th>
              <th className="px-2 pb-2 font-medium">{svcT("admin.cc.th.why", "Dispo")}</th>
            </tr></thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.service_id} onClick={() => nav("/admin/services")}
                  className="cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-800/50">
                  <td className="px-2 py-2.5 max-w-[220px] truncate text-slate-800 dark:text-slate-100">{s.name}</td>
                  {fin && <td className="px-2 py-2.5">{money(s.revenue)}</td>}
                  {fin && <td className="px-2 py-2.5">{money(s.provider_cost)}</td>}
                  {fin && <td className="px-2 py-2.5 text-amber-600 dark:text-amber-400">{money(s.funding_cost)}</td>}
                  {fin && <td className="px-2 py-2.5">{money(s.effective_cost != null ? s.effective_cost : (s.provider_cost || 0) + (s.funding_cost || 0))}</td>}
                  {fin && <td className="px-2 py-2.5">{money(s.payment_fees)}{s.payment_fees_estimated && <span className="ml-1 text-[9px] text-slate-400">est.</span>}</td>}
                  {fin && <td className="px-2 py-2.5 text-fuchsia-600 dark:text-fuchsia-400">{money(s.refunded_amount)}</td>}
                  {fin && <td className={`px-2 py-2.5 font-medium ${s.profit < 0 ? "text-rose-600 dark:text-rose-400" : ""}`}>{money(s.profit)}</td>}
                  {fin && <td className="px-2 py-2.5">{fmtPct(s.margin)}</td>}
                  <td className="px-2 py-2.5">{fmtNum(s.orders)}</td>
                  <td className="px-2 py-2.5 text-emerald-600 dark:text-emerald-400">{fmtNum(s.completed)}</td>
                  <td className="px-2 py-2.5 text-indigo-600 dark:text-indigo-400">{fmtNum(s.partial)}</td>
                  <td className="px-2 py-2.5 text-rose-600 dark:text-rose-400">{fmtNum(s.failed)}</td>
                  <td className="px-2 py-2.5">
                    <button data-testid={`cc-svc-why-${s.service_id}`} onClick={(e) => { e.stopPropagation(); setWhyId(s.service_id); }}
                      className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-blue-600 hover:bg-blue-50 dark:border-slate-700 dark:text-blue-400 dark:hover:bg-slate-800">
                      {svcT("admin.cc.why_btn", "Pourquoi ?")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
    <WhyServiceDialog sid={whyId} onClose={() => setWhyId(null)} />
    </>
  );
}

// ---------- Customer Overview ---------------------------------------------
function CustomerOverview({ customers, fin, nav }) {
  if (!customers) return null;
  const top = customers.top || [];
  return (
    <Section testid="cc-customers" title="Customer Overview" icon={Users}
      action={<button onClick={() => nav("/admin/users")} className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">Clients →</button>}>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[["Total", customers.total], ["Actifs", customers.active], ["Nouveaux", customers.new], ["Avec solde", customers.with_balance]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-200 p-3 text-center dark:border-slate-800">
            <div className="font-display text-lg font-bold text-slate-900 dark:text-white">{fmtNum(v)}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">{l}</div>
          </div>
        ))}
      </div>
      <h4 className="mb-2 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Top clients par revenu</h4>
      {top.length === 0 ? <NoData /> : (
        <div className="space-y-1.5">
          {top.map((c) => (
            <div key={c.user_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-2.5 dark:border-slate-800">
              <div className="min-w-0"><div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{c.name}</div>
                <div className="text-[11px] text-slate-400">{fmtNum(c.orders)} cmd · {fmtDate(c.last_activity)}</div></div>
              {fin && <span className="shrink-0 text-sm font-semibold text-slate-700 dark:text-slate-200">{money(c.revenue)}</span>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------- Support -------------------------------------------------------
function SupportSection({ support, nav }) {
  if (!support) return null;
  return (
    <Section testid="cc-support" title="Support & Agents" icon={LifeBuoy}
      action={<button onClick={() => nav("/admin/support")} className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">Support →</button>}>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[["Ouverts", support.open], ["FR", support.by_lang?.fr], ["EN", support.by_lang?.en], ["SW", support.by_lang?.sw]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-200 p-3 text-center dark:border-slate-800">
            <div className="font-display text-lg font-bold text-slate-900 dark:text-white">{fmtNum(v)}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">{l}</div>
          </div>
        ))}
      </div>
      <h4 className="mb-2 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">Charge par agent</h4>
      {(support.agents || []).length === 0 ? <NoData label="Aucun agent" /> : (
        <div className="space-y-1.5">
          {support.agents.map((a, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-2.5 text-sm dark:border-slate-800">
              <span className="truncate text-slate-800 dark:text-slate-100">{a.name} <span className="text-[11px] text-slate-400">{a.role || ""}</span></span>
              <span className="text-slate-600 dark:text-slate-300">{fmtNum(a.open_tickets)} ouvert(s)</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------- Live Activity -------------------------------------------------
function LiveActivity({ activity }) {
  const events = activity?.events || [];
  const kindTint = { order: "text-indigo-500", payment: "text-emerald-500", audit: "text-slate-400" };
  return (
    <Section testid="cc-activity" title="Live Activity" icon={Activity}
      action={<span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> auto 10s</span>}>
      {events.length === 0 ? <NoData label="Aucune activité récente" /> : (
        <div className="max-h-[360px] space-y-1 overflow-y-auto">
          {events.map((e, i) => (
            <div key={i} className="flex items-center gap-3 border-b border-slate-100 py-2 text-sm last:border-0 dark:border-slate-800/60">
              <span className="w-24 shrink-0 text-[11px] text-slate-400">{fmtDate(e.time)}</span>
              <span className={`truncate font-medium ${kindTint[e.kind] || "text-slate-600"}`}>{e.event}</span>
              <span className="ml-auto truncate text-[11px] text-slate-400">{e.entity}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------- System Health + Jobs ------------------------------------------
function SystemHealth({ system }) {
  if (!system) return null;
  const dot = { HEALTHY: "bg-emerald-500", WARNING: "bg-amber-500", ERROR: "bg-rose-500", UNKNOWN: "bg-slate-400" };
  return (
    <Section testid="cc-system" title="System Health" icon={Cpu}>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {(system.health || []).map((h) => (
          <div key={h.name} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2.5 text-sm dark:border-slate-800">
            <span className="flex items-center gap-2 text-slate-700 dark:text-slate-200"><span className={`h-2 w-2 rounded-full ${dot[h.status] || dot.UNKNOWN}`} />{h.name}</span>
            <span className={`text-xs font-semibold ${h.status === "HEALTHY" ? "text-emerald-600 dark:text-emerald-400" : h.status === "WARNING" ? "text-amber-600 dark:text-amber-400" : h.status === "ERROR" ? "text-rose-600 dark:text-rose-400" : "text-slate-400"}`}>{h.status}</span>
          </div>
        ))}
      </div>
      <h4 className="mb-2 mt-5 flex items-center gap-2 text-xs font-semibold uppercase text-slate-500 dark:text-slate-400"><ScrollText className="h-4 w-4" /> Background Job Monitor</h4>
      {(system.jobs || []).length === 0 ? <NoData label="Aucun job" /> : (
        <div className="space-y-2">
          {system.jobs.map((j, i) => {
            const pct = j.total > 0 ? Math.round((j.done / j.total) * 100) : 0;
            return (
              <div key={i} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-800 dark:text-slate-100">{j.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${j.status === "running" ? "bg-blue-500/15 text-blue-600 dark:text-blue-400" : j.status === "done" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-slate-500/15 text-slate-500 dark:text-slate-400"}`}>{j.status}</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} /></div>
                <div className="mt-1 flex justify-between text-[11px] text-slate-400"><span>{j.progress}</span><span>{fmtDate(j.updated)}</span></div>
                {j.error && <div className="mt-1 text-[11px] text-rose-500">{j.error}</div>}
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
